import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { navLinks } from "@/mocks/landing";
import { useAuth } from "@/hooks/useAuth";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkColor = scrolled
    ? "text-foreground-700 hover:text-primary-600"
    : "text-background-50/80 hover:text-background-50";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-xl border-b border-background-200/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="flex items-center justify-between h-16 md:h-20 px-4 md:px-8">
        <Logo dark={!scrolled} />

        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className={`text-sm font-medium whitespace-nowrap transition-colors ${linkColor}`}
              >
                {t(link.labelKey)}
              </a>
            </li>
          ))}
          <li>
            <Link to="/generate" className={`text-sm font-medium whitespace-nowrap transition-colors ${linkColor}`}>
              {t("common.nav.generate")}
            </Link>
          </li>
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher dark={!scrolled} />
          {user ? (
            <Link
              to="/profile"
              className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors ${linkColor}`}
            >
              <i className="ri-user-3-line"></i>
              {t("common.nav.profile")}
            </Link>
          ) : (
            <Link to="/auth" className={`text-sm font-medium whitespace-nowrap transition-colors ${linkColor}`}>
              {t("common.nav.signIn")}
            </Link>
          )}
          <Link
            to="/generate"
            className="whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.03] cursor-pointer"
          >
            {t("common.nav.generateNow")}
          </Link>
        </div>

        <button
          type="button"
          aria-label={t("common.nav.toggleMenu")}
          onClick={() => setMenuOpen((v) => !v)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md ${
            scrolled ? "text-foreground-900" : "text-background-50"
          }`}
        >
          <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </nav>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <LanguageSwitcher className="mb-2" />
          <ul className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 text-foreground-700 font-medium hover:text-primary-600"
                >
                  {t(link.labelKey)}
                </a>
              </li>
            ))}
            <li>
              <Link
                to="/generate"
                onClick={() => setMenuOpen(false)}
                className="block py-3 text-foreground-700 font-medium hover:text-primary-600"
              >
                {t("common.nav.generate")}
              </Link>
            </li>
            <li>
              {user ? (
                <Link
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 text-foreground-700 font-medium hover:text-primary-600"
                >
                  {t("common.nav.profile")}
                </Link>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 text-foreground-700 font-medium hover:text-primary-600"
                >
                  {t("common.nav.signIn")}
                </Link>
              )}
            </li>
          </ul>
          <Link
            to="/generate"
            onClick={() => setMenuOpen(false)}
            className="mt-3 block text-center rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3 text-sm font-semibold text-background-50"
          >
            {t("common.nav.generateNow")}
          </Link>
        </div>
      )}
    </header>
  );
}